/**
 * Asset Templates Configuration - Field Service Edition
 * 
 * SuddyFlow is built for field service businesses - companies that travel
 * TO their customers to perform work. This config defines property and
 * equipment tracking for these industries.
 * 
 * Usage:
 * - Import getAssetTypesForIndustry() to get relevant asset types
 * - Import getAssetFieldSchema() to get field definitions for a type
 * - If no assets configured for an industry, the feature is hidden
 */

// ============================================
// ASSET TYPE DEFINITIONS
// ============================================

export const AssetTypes = {
  Property: 'Property',
  Equipment: 'Equipment',
};

// ============================================
// FIELD SCHEMAS FOR EACH ASSET TYPE
// ============================================

/**
 * Field schema defines what fields to show in the asset form.
 * Each field has:
 * - key: JSON attribute name
 * - label: Display label
 * - type: input type (text, number, date, select, textarea)
 * - options: for select type, array of { value, label }
 * - placeholder: input placeholder
 * - required: whether field is required
 * - helpText: optional help text
 */

const US_STATE_OPTIONS = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
].map((code) => ({ value: code, label: code }));

export const assetFieldSchemas = {
  Property: {
    icon: 'Home',
    displayName: 'Property',
    namePlaceholder: 'Main Residence',
    nameHelpText: 'A name to identify this property',
    fields: [
      { key: 'address', label: 'Street Address', type: 'text', placeholder: '123 Main St', required: false },
      { key: 'city', label: 'City', type: 'text', placeholder: 'Mooresville', required: false },
      { key: 'state', label: 'State', type: 'select', options: US_STATE_OPTIONS, required: false },
      { key: 'zipCode', label: 'Zip', type: 'text', placeholder: '28115', required: false },      { key: 'address', label: 'Property Address', type: 'text', placeholder: '123 Main St (if different from customer)', required: false },
      { key: 'propertyType', label: 'Property Type', type: 'select', options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' },
        { value: 'industrial', label: 'Industrial' },
        { value: 'hoa', label: 'HOA / Community' },
      ], required: false },
      { key: 'squareFeet', label: 'Building Sq Ft', type: 'number', placeholder: '2400', required: false },
      { key: 'lotSize', label: 'Lot Size (acres)', type: 'number', placeholder: '0.5', required: false },
      { key: 'stories', label: 'Stories', type: 'number', placeholder: '2', required: false },
      { key: 'drivewaySquareFeet', label: 'Driveway Sq Ft', type: 'number', placeholder: '800', required: false },
      { key: 'deckSquareFeet', label: 'Deck/Patio Sq Ft', type: 'number', placeholder: '200', required: false },
      { key: 'fenceLinearFeet', label: 'Fence Linear Ft', type: 'number', placeholder: '150', required: false },
      { key: 'roofSquareFeet', label: 'Roof Sq Ft', type: 'number', placeholder: '2800', required: false },
      { key: 'poolSquareFeet', label: 'Pool Sq Ft', type: 'number', placeholder: '400', required: false },
      { key: 'gateCode', label: 'Gate Code', type: 'text', placeholder: '#1234', required: false },
      { key: 'lockboxCode', label: 'Lockbox Code', type: 'text', placeholder: '5678', required: false },
      { key: 'accessNotes', label: 'Access Notes', type: 'textarea', placeholder: 'Gate on left side, dog in backyard (friendly)', required: false },
    ],
  },

  Equipment: {
    icon: 'Settings',
    displayName: 'Equipment',
    namePlaceholder: 'Carrier AC Unit - Upstairs',
    nameHelpText: 'Brand and location (e.g., "Carrier AC Unit - Upstairs")',
    fields: [
      { key: 'equipmentType', label: 'Equipment Type', type: 'select', options: [
        { value: 'hvac_ac', label: 'Air Conditioner' },
        { value: 'hvac_furnace', label: 'Furnace' },
        { value: 'hvac_heatpump', label: 'Heat Pump' },
        { value: 'hvac_handler', label: 'Air Handler' },
        { value: 'pool_pump', label: 'Pool Pump' },
        { value: 'pool_filter', label: 'Pool Filter' },
        { value: 'pool_heater', label: 'Pool Heater' },
        { value: 'pool_cleaner', label: 'Pool Cleaner' },
        { value: 'water_heater', label: 'Water Heater' },
        { value: 'water_softener', label: 'Water Softener' },
        { value: 'sprinkler_controller', label: 'Sprinkler Controller' },
        { value: 'generator', label: 'Generator' },
        { value: 'other', label: 'Other' },
      ], required: false },
      { key: 'manufacturer', label: 'Manufacturer', type: 'text', placeholder: 'Carrier', required: false },
      { key: 'model', label: 'Model Number', type: 'text', placeholder: '24ACC636A003', required: false },
      { key: 'serialNumber', label: 'Serial Number', type: 'text', placeholder: '1234567890', required: false },
      { key: 'installDate', label: 'Install Date', type: 'date', required: false },
      { key: 'warrantyExpires', label: 'Warranty Expires', type: 'date', required: false },
      { key: 'lastServiceDate', label: 'Last Service Date', type: 'date', required: false },
      { key: 'tonnage', label: 'Tonnage/Capacity', type: 'text', placeholder: '3 ton', required: false },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Upstairs closet', required: false },
      { key: 'filterSize', label: 'Filter Size', type: 'text', placeholder: '20x25x1', required: false },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Any special notes about this equipment', required: false },
    ],
  },
};

// ============================================
// INDUSTRY -> ASSET TYPE MAPPING
// ============================================

/**
 * Maps field service industry types to their relevant asset types.
 * Order matters - first type is the default/primary.
 * Empty array = assets feature hidden for this industry.
 * 
 * All industries listed here are FIELD SERVICE (travel to customer).
 */
export const industryAssetTypes = {
  // Exterior Cleaning / Property Services
  PressureWashing: [AssetTypes.Property],
  WindowWashing: [AssetTypes.Property],
  GutterCleaning: [AssetTypes.Property],
  RoofCleaning: [AssetTypes.Property],
  
  // Interior Services
  HouseCleaning: [AssetTypes.Property],
  CarpetCleaning: [AssetTypes.Property],
  
  // Lawn & Landscape
  Landscaping: [AssetTypes.Property],
  TreeService: [AssetTypes.Property],
  
  // Specialty Services
  ChristmasLights: [AssetTypes.Property],
  GlassMirror: [AssetTypes.Property],
  
  // Trades (Service Calls)
  HVAC: [AssetTypes.Equipment, AssetTypes.Property],
  Plumbing: [AssetTypes.Property],
  Electrical: [AssetTypes.Property],
  Handyman: [AssetTypes.Property],
  
  // Pool & Pest
  PoolService: [AssetTypes.Equipment, AssetTypes.Property],
  PestControl: [AssetTypes.Property],
  
  // Per-Job Services (no recurring asset tracking needed)
  JunkRemoval: [],
  
  // Catch-all for other field service businesses
  Other: [AssetTypes.Property],
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get relevant asset types for an industry.
 * Returns empty array if assets aren't configured for this industry.
 */
export const getAssetTypesForIndustry = (industryType) => {
  return industryAssetTypes[industryType] || [];
};

/**
 * Check if an industry uses assets at all.
 * Use this to conditionally show/hide the assets tab.
 */
export const industryUsesAssets = (industryType) => {
  const types = getAssetTypesForIndustry(industryType);
  return types.length > 0;
};

/**
 * Get the field schema for an asset type.
 */
export const getAssetFieldSchema = (assetType) => {
  return assetFieldSchemas[assetType] || assetFieldSchemas.Property;
};

/**
 * Get the default asset type for an industry.
 */
export const getDefaultAssetType = (industryType) => {
  const types = getAssetTypesForIndustry(industryType);
  return types.length > 0 ? types[0] : AssetTypes.Property;
};

/**
 * Generate a smart default name for a vehicle asset.
 * @deprecated Vehicle assets removed - kept for backwards compatibility
 */
export const generateVehicleName = (attributes) => {
  const parts = [];
  if (attributes?.year) parts.push(attributes.year);
  if (attributes?.make) parts.push(attributes.make);
  if (attributes?.model) parts.push(attributes.model);
  return parts.join(' ') || 'Unnamed Vehicle';
};

// ============================================
// SERVICE -> PROPERTY MEASUREMENT MAPPING
// ============================================

// Which Property field a service's measurement belongs to. Services that match nothing
// never read or write the Property. Order matters: first match wins.
const PROPERTY_MEASURE_RULES = [
  { match: /driveway/i, key: 'drivewaySquareFeet', type: 'sqft', label: 'driveway' },
  { match: /deck|patio/i, key: 'deckSquareFeet', type: 'sqft', label: 'deck/patio' },
  { match: /fence/i, key: 'fenceLinearFeet', type: 'linear', label: 'fence' },
  { match: /roof/i, key: 'roofSquareFeet', type: 'sqft', label: 'roof' },
  { match: /pool/i, key: 'poolSquareFeet', type: 'sqft', label: 'pool' },
  { match: /house\s*wash|home\s*wash|siding/i, key: 'squareFeet', type: 'sqft', label: 'building' },
];

export const getPropertyMeasureTarget = (serviceName, measurementType) => {
  const rule = PROPERTY_MEASURE_RULES.find((r) => r.match.test(serviceName || ''));
  if (!rule || rule.type !== measurementType) return null;
  return { key: rule.key, label: rule.label, unit: rule.type === 'linear' ? 'linear ft' : 'sq ft' };
};

// Matches modifiers named "2-Story", "Two Story", "2 story home", "Second story"
export const isStoryModifier = (name) =>
  /(^|\W)(2|two)[\s-]*stor(y|ey)|second[\s-]*stor/i.test(name || '');

/**
 * Parse attributes JSON safely
 */
export const parseAttributes = (attributesJson) => {
  if (!attributesJson) return {};
  try {
    return JSON.parse(attributesJson);
  } catch {
    return {};
  }
};

/**
 * Stringify attributes for storage
 */
export const stringifyAttributes = (attributes) => {
  if (!attributes || Object.keys(attributes).length === 0) return null;
  return JSON.stringify(attributes);
};

// ============================================
// FIELD SERVICE INDUSTRIES LIST
// ============================================

/**
 * Master list of supported field service industries.
 * Used for validation and reference.
 */
export const fieldServiceIndustries = [
  { value: 'PressureWashing', label: 'Pressure Washing' },
  { value: 'WindowWashing', label: 'Window Cleaning' },
  { value: 'GutterCleaning', label: 'Gutter Cleaning' },
  { value: 'RoofCleaning', label: 'Roof Cleaning' },
  { value: 'HouseCleaning', label: 'House Cleaning' },
  { value: 'CarpetCleaning', label: 'Carpet Cleaning' },
  { value: 'Landscaping', label: 'Landscaping' },
  { value: 'TreeService', label: 'Tree Service' },
  { value: 'ChristmasLights', label: 'Christmas Lights' },
  { value: 'GlassMirror', label: 'Glass & Mirror' },
  { value: 'HVAC', label: 'HVAC' },
  { value: 'Plumbing', label: 'Plumbing' },
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Handyman', label: 'Handyman' },
  { value: 'PoolService', label: 'Pool Service' },
  { value: 'PestControl', label: 'Pest Control' },
  { value: 'JunkRemoval', label: 'Junk Removal' },
  { value: 'Other', label: 'Other Field Service' },
];

/**
 * Check if an industry type is a valid field service industry
 */
export const isValidFieldServiceIndustry = (industryType) => {
  return fieldServiceIndustries.some(i => i.value === industryType);
};

export default {
  AssetTypes,
  assetFieldSchemas,
  industryAssetTypes,
  getAssetTypesForIndustry,
  industryUsesAssets,
  getAssetFieldSchema,
  getDefaultAssetType,
  generateVehicleName,
  parseAttributes,
  stringifyAttributes,
  fieldServiceIndustries,
  isValidFieldServiceIndustry,
};